import { EXPORT_FIELDS } from 'constants/export_csv';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

type ErrorObject = {
  errorMessage: string;
  errorType: string;
  row: number;
};

type EventParams = {
  [EXPORT_FIELDS.TITLE]: string;
  [EXPORT_FIELDS.DESCRIPTION]: string;
  [EXPORT_FIELDS.LOCATION]?: string;
  [EXPORT_FIELDS.DATE]: string;
  [EXPORT_FIELDS.FROM]: string;
  [EXPORT_FIELDS.TO]: string;
  [EXPORT_FIELDS.EVENT_TYPE_NAME]: string;
  [EXPORT_FIELDS.ALL_DAY]: string;
  row: number;
};

export const useValidateEventData: () => {
  dataValidation: (data: EventParams) => ErrorObject | null;
} = () => {
  const { t } = useTranslation();

  const dataValidation: (data: EventParams) => ErrorObject | null = ({
    [EXPORT_FIELDS.DESCRIPTION]: description,
    [EXPORT_FIELDS.EVENT_TYPE_NAME]: eventTypeName,
    [EXPORT_FIELDS.DATE]: date,
    [EXPORT_FIELDS.FROM]: from,
    [EXPORT_FIELDS.TO]: to,
    [EXPORT_FIELDS.LOCATION]: location,
    [EXPORT_FIELDS.TITLE]: title,
    [EXPORT_FIELDS.ALL_DAY]: allDay,
    row,
  }) => {
    let error: ErrorObject = {
      errorMessage: '',
      errorType: '',
      row: 0,
    };
    if (!title) {
      error = {
        ...error,
        errorMessage: t('bulkUploadEvents.errors.eventNameErrorMessage', {
          row: row + 1,
        }),
        errorType: t('bulkUploadEvents.errors.eventNameErrorTitle'),
        row,
      };
      return error;
    }
    if (!eventTypeName) {
      error = {
        ...error,
        errorMessage: t('bulkUploadEvents.errors.eventTypeErrorMessage', {
          row: row + 1,
        }),
        errorType: t('bulkUploadEvents.errors.eventTypeErrorTitle'),
        row,
      };
      return error;
    }
    // check if date field is empty
    if (!from || !to) {
      error = {
        ...error,
        errorMessage: t('bulkUploadEvents.errors.timeErrorMessage', {
          row: row + 1,
        }),
        errorType: t('bulkUploadEvents.errors.timeErrorTitle'),
        row,
      };
      return error;
    }

    if (!date) {
      error = {
        errorMessage: t('bulkUploadEvents.errors.dateErrorMessage', {
          row: row + 1,
        }),
        errorType: t('bulkUploadEvents.errors.dateErrorTitle'),
        row,
      };
      return error;
    }
    // check if date field is valid
    if (to && from && date) {
      const eventDate = moment(date, 'DD-MM-YYYY');
      const tempDate = moment(date);
      let formattedDate = '';
      if (eventDate.isValid() || tempDate.isValid()) {
        if (eventDate.isValid()) {
          formattedDate = eventDate.format('LL');
        } else if (tempDate.isValid()) {
          formattedDate = tempDate.format('LL');
        }
      }

      const startTime = moment(formattedDate + ' ' + from);
      const endTime = moment(formattedDate + ' ' + to);
      if (
        !startTime.isValid() ||
        !endTime.isValid() ||
        (!eventDate.isValid() && !tempDate.isValid())
      ) {
        error = {
          errorMessage: t('bulkUploadEvents.errors.invalidDateOrTimeMessage', {
            row: row + 1,
          }),
          errorType: t('bulkUploadEvents.errors.invalidDateOrTimeTitle'),
          row,
        };
        return error;
      }
    }

    if (!location) {
      error = {
        ...error,
        errorType: t('bulkUploadEvents.errors.locationErrorTitle'),
        errorMessage: t('bulkUploadEvents.errors.locationErrorMessage', {
          row: row + 1,
        }),
        row,
      };
      return error;
    }
    return null;
  };

  return { dataValidation };
};

export const exportFileData = (
  data: BlobPart,
  fileName: string,
  contentType: string,
) => {
  const blob = new Blob([data], { type: contentType });
  const url = URL.createObjectURL(blob);
  const downloadButton = document.createElement('a');
  downloadButton.href = url;
  downloadButton.setAttribute('download', fileName);
  downloadButton.click();
  URL.revokeObjectURL(url);
  downloadButton.remove();
};

let utf8FatalDecoderWorks: boolean | null = null;

function utf8StrictDecodeSupported(): boolean {
  if (utf8FatalDecoderWorks !== null) return utf8FatalDecoderWorks;
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array([0xff]));
    utf8FatalDecoderWorks = false;
  } catch {
    utf8FatalDecoderWorks = true;
  }
  return utf8FatalDecoderWorks;
}

export async function decodeImportedCsvFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(bytes);
  }

  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(bytes);
  }

  if (
    bytes.length >= 3 &&
    bytes[0] === 0xef &&
    bytes[1] === 0xbb &&
    bytes[2] === 0xbf
  ) {
    return new TextDecoder('utf-8').decode(bytes.subarray(3));
  }

  if (utf8StrictDecodeSupported()) {
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      return new TextDecoder('windows-1252').decode(bytes);
    }
  }

  const asUtf8 = new TextDecoder('utf-8').decode(bytes);
  if (asUtf8.includes('\uFFFD')) {
    return new TextDecoder('windows-1252').decode(bytes);
  }
  return asUtf8;
}
